import { useState, useEffect, useRef } from 'react';

export default function DemoSection() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = new URLSearchParams();

    formData.forEach((value, key) => {
      data.append(key, value as string);
    });

    fetch('https://readdy.ai/api/form/d7o69nimsguiojolf80g', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: data.toString(),
    })
      .then(() => {
        setFormStatus('success');
        form.reset();
      })
      .catch(() => {
        setFormStatus('error');
      });
  };

  const inputClasses = (fieldName: string) =>
    `w-full px-4 py-3 bg-firmai-dark border rounded-md text-white text-sm placeholder-firmai-muted focus:outline-none transition-all duration-300 ${
      focusedField === fieldName
        ? 'border-firmai-green shadow-[0_0_0_3px_rgba(0,217,163,0.1)]'
        : 'border-firmai-border hover:border-firmai-border/80'
    }`;

  return (
    <section
      id="demo"
      ref={sectionRef}
      className="relative py-20 md:py-28 overflow-hidden"
    >
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-firmai-dark via-firmai-darker to-firmai-dark" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,217,163,0.08)_0%,transparent_70%)]" />

      {/* Floating orbs */}
      <div className="absolute top-1/4 left-[10%] w-32 h-32 bg-firmai-green/5 rounded-full blur-2xl animate-pulse" />
      <div className="absolute bottom-1/4 right-[10%] w-40 h-40 bg-firmai-blue/5 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '1.5s' }} />

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="reveal opacity-0 translate-y-6 transition-all duration-700 text-center mb-12">
            <h2 className="font-display font-bold text-2xl md:text-4xl text-white leading-tight">
              开启您的能源
              <br className="md:hidden" />
              数字化转型之旅
            </h2>
            <p className="text-firmai-text mt-4 text-sm md:text-base">
              专业团队提供一对一演示，定制化解决方案咨询
            </p>
          </div>

          {/* Form Card */}
          <div className="reveal opacity-0 translate-y-6 transition-all duration-700 delay-100">
            <div className="bg-firmai-card border border-firmai-border rounded-xl p-6 md:p-10 hover:border-firmai-green/10 transition-all duration-500">
              {formStatus === 'success' ? (
                <div className="text-center py-8 animate-scale-in">
                  <span className="w-16 h-16 flex items-center justify-center mx-auto text-firmai-green mb-4">
                    <i className="ri-checkbox-circle-line text-5xl animate-bounce-soft" />
                  </span>
                  <h3 className="text-white font-semibold text-xl mb-2">提交成功！</h3>
                  <p className="text-firmai-text">我们的专业顾问将在24小时内与您联系</p>
                  <button
                    onClick={() => setFormStatus('idle')}
                    className="mt-6 px-6 py-2.5 text-sm text-firmai-green border border-firmai-green/30 rounded-md hover:bg-firmai-green/10 transition-all hover:scale-105 active:scale-95"
                  >
                    再次预约
                  </button>
                </div>
              ) : (
                <form
                  id="demo-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-firmai-text text-sm mb-2">姓名 *</label>
                      <input
                        type="text"
                        name="name"
                        required
                        onFocus={() => setFocusedField('name')}
                        onBlur={() => setFocusedField(null)}
                        className={inputClasses('name')}
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label className="block text-firmai-text text-sm mb-2">公司名称 *</label>
                      <input
                        type="text"
                        name="company"
                        required
                        onFocus={() => setFocusedField('company')}
                        onBlur={() => setFocusedField(null)}
                        className={inputClasses('company')}
                        placeholder="请输入公司名称"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-firmai-text text-sm mb-2">工作邮箱 *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        onFocus={() => setFocusedField('email')}
                        onBlur={() => setFocusedField(null)}
                        className={inputClasses('email')}
                        placeholder="your@company.com"
                      />
                    </div>
                    <div>
                      <label className="block text-firmai-text text-sm mb-2">联系电话</label>
                      <input
                        type="tel"
                        name="phone"
                        onFocus={() => setFocusedField('phone')}
                        onBlur={() => setFocusedField(null)}
                        className={inputClasses('phone')}
                        placeholder="请输入联系电话"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-firmai-text text-sm mb-2">感兴趣的功能</label>
                    <select
                      name="interest"
                      onFocus={() => setFocusedField('interest')}
                      onBlur={() => setFocusedField(null)}
                      className={`${inputClasses('interest')} appearance-none cursor-pointer`}
                    >
                      <option value="">请选择...</option>
                      <option value="monitoring">实时监测仪表盘</option>
                      <option value="ai-analysis">AI智能分析</option>
                      <option value="predictive">预测性维护</option>
                      <option value="carbon">碳排放追踪</option>
                      <option value="green-factory">绿色工厂申报</option>
                      <option value="ccer">CCER碳汇开发</option>
                      <option value="cbam">CBAM碳边境调节</option>
                      <option value="integration">企业工具集成</option>
                      <option value="reports">智能报告生成</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-firmai-text text-sm mb-2">留言（可选）</label>
                    <textarea
                      name="message"
                      rows={3}
                      maxLength={500}
                      onFocus={() => setFocusedField('message')}
                      onBlur={() => setFocusedField(null)}
                      className={`${inputClasses('message')} resize-none`}
                      placeholder="请描述您的需求或问题..."
                    />
                    <p className="text-firmai-muted text-xs mt-1">最多500字符</p>
                  </div>

                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-white text-firmai-dark font-semibold rounded-md hover:bg-firmai-green transition-all duration-200 disabled:opacity-50 whitespace-nowrap hover:scale-105 active:scale-95 group"
                  >
                    {formStatus === 'submitting' ? (
                      <>
                        <span className="w-5 h-5 flex items-center justify-center animate-spin">
                          <i className="ri-loader-4-line" />
                        </span>
                        提交中...
                      </>
                    ) : (
                      <>
                        <span className="w-5 h-5 flex items-center justify-center bg-firmai-green rounded-full text-firmai-dark group-hover:bg-firmai-dark group-hover:text-firmai-green transition-all duration-200">
                          <i className="ri-calendar-line text-sm" />
                        </span>
                        预约免费演示
                        <span className="w-5 h-5 flex items-center justify-center group-hover:translate-x-1 transition-transform duration-200">
                          <i className="ri-arrow-right-line" />
                        </span>
                      </>
                    )}
                  </button>

                  {formStatus === 'error' && (
                    <p className="text-red-400 text-sm animate-shake">提交失败，请稍后重试</p>
                  )}
                </form>
              )}
            </div>
          </div>

          {/* Contact Info */}
          <div className="reveal opacity-0 translate-y-6 transition-all duration-700 delay-200 text-center mt-8">
            <p className="text-firmai-muted text-sm">
              或拨打 <span className="text-firmai-green font-medium">18916035855</span> 咨询
            </p>
          </div>
        </div>
      </div>

      <style>{`
        .reveal.animate-in {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
        @keyframes scale-in {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .animate-scale-in {
          animation: scale-in 0.4s ease-out;
        }
        @keyframes bounce-soft {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        .animate-bounce-soft {
          animation: bounce-soft 1s ease-in-out;
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        .animate-shake {
          animation: shake 0.3s ease-in-out;
        }
      `}</style>
    </section>
  );
}